import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import { withRouter } from 'react-router-dom';

import { StaticContext, ErrorCard } from '@sm/webassets';
import { defineMessages, t, T } from '@sm/intl';
import { Typography } from '@sm/wds-react';

import publishedApplicationListingsQuery from './AppsGrid.graphql';
import AppsGrid from './AppsGrid';
import AppsGridSkeleton from './AppsGridSkeleton';

const COPY = defineMessages({
  FEATURED_APPS_TITLE: {
    id: 'Home.AppsGrid.FeaturedTitle',
    defaultMessage: 'Featured',
    description:
      '[Type: Header][Vis.: high] - A section title for a grid displaying the featured apps'
  },
  RECENTLY_ADDED_APPS_TITLE: {
    id: 'Home.AppsGrid.RecentlyAddedTitle',
    defaultMessage: 'Recently Added',
    description:
      '[Type: Header][Vis.: high] - A section title for a grid displaying the recently apps'
  },
  MOST_RECENT_APPS_TITLE: {
    id: 'Home.AppsGrid.MostRecentTitle',
    defaultMessage: 'Most Popular',
    description:
      '[Type: Header][Vis.: high] - A section title for a grid displaying the most popular apps'
  },
  GRID_APPS_SEE_MORE: {
    id: 'Home.AppsGrid.SeeMore',
    defaultMessage: 'See More',
    description:
      '[Type: Link][Vis.: med] - A link to redirect the user so it can see more apps from the current displayed category'
  }
});

const FEATURED = 'FEATURED';
const RECENTLY_ADDED = 'RECENTLY_ADDED';
const MOST_POPULAR = 'MOST_POPULAR';

const retrieveValuesForGridOption = option => {
  const page = 1;
  const defaultPageSize = 6;
  switch (option) {
    case FEATURED:
      return {
        title: t(COPY.FEATURED_APPS_TITLE),
        moreLink: '/apps/featured-apps',
        page,
        pageSize: defaultPageSize
      };

    case RECENTLY_ADDED:
      return {
        title: t(COPY.RECENTLY_ADDED_APPS_TITLE),
        moreLink: '/apps/recently-added',
        page,
        pageSize: defaultPageSize
      };

    case MOST_POPULAR:
      return {
        title: t(COPY.MOST_RECENT_APPS_TITLE),
        moreLink: '/apps/most-popular',
        page,
        pageSize: 9
      };

    default:
      return ['', '#', page, defaultPageSize];
  }
};

const AppsGridContainer = ({ gridOption, location: { search } }) => {
  const { title, moreLink, page, pageSize } = retrieveValuesForGridOption(
    gridOption
  );
  const {
    environment: { languageId }
  } = useContext(StaticContext);
  return (
    <Query
      query={publishedApplicationListingsQuery}
      variables={{ language: languageId, filter: gridOption, page, pageSize }}
    >
      {({ loading, error, data }) => {
        return (
          <section className="sm-apps-grid">
            <header className="sm-apps-grid__header wds-flex wds-flex--between">
              <Typography component="div" variant="cardTitle">
                {title}
              </Typography>
              <If condition={moreLink}>
                <Typography>
                  {/* The query params need to be preserved between urls (needed for ut_source) */}
                  <a
                    href={`${moreLink}${search}`}
                    className="sm-apps-grid__header-more-link"
                  >
                    <T desc={COPY.GRID_APPS_SEE_MORE} />
                  </a>
                </Typography>
              </If>
            </header>
            <Choose>
              <When condition={error}>
                <ErrorCard />
              </When>
              <When condition={loading}>
                <AppsGridSkeleton />
              </When>
              <Otherwise>
                <AppsGrid apps={data.publishedApplicationListings.items} />
              </Otherwise>
            </Choose>
          </section>
        );
      }}
    </Query>
  );
};

AppsGridContainer.propTypes = {
  environment: PropTypes.shape({
    languageId: PropTypes.number.isRequired
  }),
  gridOption: PropTypes.oneOf([FEATURED, MOST_POPULAR, RECENTLY_ADDED])
    .isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired
};

AppsGridContainer.defaultProps = {
  environment: {
    languageId: 1
  }
};

export default withRouter(AppsGridContainer);
